import { ETextOrLink } from '@components/common/ParagraphWithInternalLinks/ParagraphWithInternalLinks.enum';
import { EStrapiRichText } from '@components/common/StrapiRichText/StrapiRichText.enum';
import { IStrapiRichText } from '@components/common/StrapiRichText/StrapiRichText.type';
import { ESocialLoginPlatformsDisplayName } from 'src/features/settings/features/settingsSocialRedirection/components/SocialRedirectionPage/SettingsSocialRedirectionPage.enums';

const ERROR = {
	generic: 'Our server is unavailable at the moment, please try again later',
	genericToastTitle: 'Oh oh, something went wrong',
	settings: {
		lightMode: 'Error: You fool, there is only darkness in the realm of the Node Guardians!'
	},
	socials: {
		deniedByAuthServer: 'The authorization server denied the request'
	},
	devHub: {
		filteringQuests: 'Error while filtering quests',
		filteringCampaigns: 'Error while filtering campaigns',
		loadingQuests: 'Error while loading quests',
		loadingCampaigns: 'Error while loading campaigns',
		fetchingQuestDetails: 'Error while fetching quest details'
	},
	character: {
		loadingInventoryItems: 'Error while loading inventory items',
		loadingWornItems: 'Error while loading worn items',
		equippingItem: 'Error while equipping item',
		unequippingItem: 'Error while unequipping item',
		savingAvatarType: 'Error while saving avatar type',
		forgingItem: 'Error while forging item',
		openingChests: 'Error while opening chests',
		openingChest: 'Error while opening chest'
	},
	auth: {
		connectingWallet: 'Error while connecting',
		userRejectedSigning: 'Ups, apparently you rejected signing, please try again'
	},
	singleQuest: {
		fetchingUserQuiz: 'Error while fetching user quiz',
		checkingContractExistence: 'Error while checking contract existence',
		gettingContractName: 'Error while getting contract name',
		contractDeploymentFailed: 'Error while deploying contract'
	},
	avatarCustomization: {
		previewingItems: 'Error while previewing items'
	},
	transactionRejected: {
		title: 'Transaction Rejected',
		message: 'The transaction was rejected by the user'
	},
	faucet: {
		sent: {
			title: (ticker?: string) => `${ticker} transaction failed`,
			fallbackTitle: 'Testnet tokens transaction failed',
			message: (explorerUrl: string, linkLabel: string): IStrapiRichText[] => [
				{
					id: 'faucet-event-1',
					type: EStrapiRichText.paragraphWithLinks,
					data: {
						textAndInternalLinks: [
							{ id: 1, type: ETextOrLink.text, value: 'See on explorer: ' },
							{ id: 2, type: ETextOrLink.externalLink, value: linkLabel, href: explorerUrl }
						]
					}
				}
			],
			fallbackMessage: 'Testnet tokens transaction failed'
		}
	}
};

const SUCCESS = {
	generic: 'Success',
	socials: {
		successfulConnection: (platform: ESocialLoginPlatformsDisplayName) =>
			`Connection with ${platform} account successful`
	},
	github: {
		appInstalled: "App installed, you're ready to go!",
		appRemoved: 'App removed',
		accountLinked: 'Logged in with your GitHub account',
		usernameUpdated: (username: string) => `GitHub username updated to ${username}`,
		repositoryChanged: (status: string) => `Repository ${status}`
	},
	discord: {
		newRole: {
			title: 'New Discord role',
			message: 'Visit Discord to check out your new role.'
		},
		roleRemoved: {
			title: 'Discord role removed',
			message: 'Your Discord role has been removed.'
		},
		usernameUpdated: (username: string) => `Discord username updated to ${username}`
	},
	devHub: {
		campaigns: {
			started: (campaignName: string) => `Campaign ${campaignName} started`,
			finished: (campaignName: string) => `Campaign ${campaignName} finished`
		},
		quests: {
			started: (questName: string) => `Quest ${questName} started`,
			finished: (questName: string) => `Quest ${questName} finished`
		}
	},
	faucet: {
		sent: {
			title: (amount: string, ticker: string) => `${amount} ${ticker} sent`,
			fallbackTitle: 'Testnet tokens sent',
			message: (explorerUrl: string, linkLabel: string): IStrapiRichText[] => [
				{
					id: 'faucet-event-1',
					type: EStrapiRichText.paragraphWithLinks,
					data: {
						textAndInternalLinks: [
							{ id: 1, type: ETextOrLink.text, value: 'See on explorer: ' },
							{ id: 2, type: ETextOrLink.externalLink, value: linkLabel, href: explorerUrl }
						]
					}
				}
			],
			fallbackMessage: 'Testnet tokens sent'
		},
		sending: {
			title: (amount: string, ticker: string) => `Sending ${amount} ${ticker ? ticker + ' ' : ''}...`,
			fallbackTitle: 'Sending testnet tokens...',
			message: (explorerUrl: string, linkLabel: string): IStrapiRichText[] => [
				{
					id: 'faucet-event-1',
					type: EStrapiRichText.paragraphWithLinks,
					data: {
						textAndInternalLinks: [
							{ id: 1, type: ETextOrLink.text, value: 'See on explorer: ' },
							{ id: 2, type: ETextOrLink.externalLink, value: linkLabel, href: explorerUrl }
						]
					}
				}
			],
			fallbackMessage: 'Sending testnet tokens...'
		}
	}
};

export { ERROR, SUCCESS };
